import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type IntersectionAnimations = 'none' | 'scale-up' | 'slide-up' | 'slide-left' | 'slide-right';

interface IntersectionObserverInit {
  rootMargin?: string;
  threshold?: number | number[];
  callback?: (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => void;

  /**

   * Breakpoint for the mobile and desktop version

   */

  breakpoint?: number;

  /**

   * Callback to handle events when the element is visible or not. Expected to only handle the visibility of one element,

   * but is not restricted to that.

   */

  visibilityOutput?: BehaviorSubject<boolean>;
}

interface IntersectionObserverItem extends IntersectionObserverInit {
  observer: IntersectionObserver;
  targets: IntersectionObserverTarget[];
  breakpoint?: number;
}

interface IntersectionObserverTarget {
  id: string;
  element: HTMLElement;
  animation?: IntersectionAnimations;
  animationMobile?: IntersectionAnimations;
  aniationBackwards?: boolean;
}

/**
 * Instructions to use:
 * 1. Add the directive (IntersectionObserverDirective) to the comonent you
 * want to observe
 * 2. Crear ViewChildren of the directive in the component to get the elements
 * 3. In the injected IntersectionObserverService, call the observe method with
 * the elements and the init object
 * 4. Call the clean method when the component is destroyed
 * NOTE: the observe method returns a key that you can use to clean the observer
 * 5. If some animationns are diferent in mobile in the injected component add
 * a host listener to the window resize event and call the checkBreakpoints method
 *
 * To edit the animations check the css variables attached to the styles.
 */
const VISIBLE_CLASS = 'js-visible';
const SCROLLED_UP_CLASS = 'js-scrolled-up';
@Injectable({
  providedIn: 'root'
})
export class IntersectionObserverService {
  private readonly PLATFORM_ID = inject(PLATFORM_ID);

  private readonly isPlatformBrowser = isPlatformBrowser(this.PLATFORM_ID);

  observables: Map<string, IntersectionObserverItem> = new Map();

  constructor() {
    this.injectStyles();
  }

  observe(targets: HTMLElement[], init?: IntersectionObserverInit) {
    const idObserver = this.generateKey();
    const { visibilityOutput } = init || {};
    const defaultCallback = (entries: IntersectionObserverEntry[]) => {
      let visible = false;

      entries.forEach(entry => {
        const target = entry.target as HTMLElement;

        if (target.getAttribute('data-debug')) {
          console.log(entry);
        }

        if (target.classList.contains('intersectionAnimationDone')) return;

        target.classList.add('intersectionObserve');

        const direction = entry.boundingClientRect.y < 0 ? 'down' : 'up';

        if (direction === 'up') {
          target.setAttribute('data-intersected-direction', 'up');

          if (!target.hasAttribute('intersectionanimationbackwards')) {
            target.classList.remove(SCROLLED_UP_CLASS);
          }
        } else {
          target.setAttribute('data-intersected-direction', 'down');

          if (!target.hasAttribute('intersectionanimationbackwards')) {
            target.classList.add(SCROLLED_UP_CLASS);
          }
        }

        if (entry.isIntersecting) {
          target.classList.add(VISIBLE_CLASS);

          visible = true;

          if (target.getAttribute('intersectionanimationonce') === 'true') {
            target.classList.add('intersectionAnimationDone');
          }
        } else {
          target.classList.remove(VISIBLE_CLASS);
        }
      });

      visibilityOutput?.next(visible);
    };

    const observeElement = (target: HTMLElement, observerKey: string) => {
      const observeItem = this.observables.get(observerKey);

      if (!observeItem) return;

      target.setAttribute('data-observer-key', idObserver);

      observer.observe(target);
    };

    const { rootMargin, threshold, breakpoint = 992, callback = defaultCallback } = init || {};

    const interceptorTargets = targets.map(target => {
      const id = this.generateKey();

      const animation = target.getAttribute('intersectionanimation') as IntersectionAnimations;

      const animationMobile = target.getAttribute('intesetionanimationmobile') as IntersectionAnimations;

      const animationBackwards = target.getAttribute('intersectionanimationbackwards') ? true : false;

      return { id, element: target, animation, animationMobile, animationBackwards } as IntersectionObserverTarget;
    });

    const observer = new IntersectionObserver(callback, {
      rootMargin,

      threshold
    });

    this.observables.set(idObserver, { observer, breakpoint, targets: interceptorTargets });

    targets.forEach(target => {
      observeElement(target, idObserver);
    });

    this.checkBreakpoints();

    return idObserver;
  }

  clean(keys: string[]) {
    keys.forEach(key => {
      const observable = this.observables.get(key);

      if (observable) {
        observable.observer.disconnect();
      }

      this.observables.delete(key);
    });
  }

  /**

   * Be careful with edit this part.

   * To debug copy the innerHTML output and paste in as new file in the editor,

   * that should show errors.

   */

  private injectStyles() {
    if (!this.isPlatformBrowser) return;

    const style = document.createElement('style');

    // DEFAULT VALUES

    const scaleStart = '0.66666';

    const slideVerticalOffset = '100px';

    const slideHorizontalOffset = '100px';

    const opacityStart = '0';

    style.innerHTML = `



:root {



  --intersection-animation__transition-transform: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;



}







.intersectionObserve {



  transition: var(--intersection-animation__transition-transform);



}



  `;

    // SCALE UP

    style.innerHTML += `



.intersectionObserve[data-intersection-ananimation="scale-up"]:not(.js-visible) {



	transform: scale(--intersection-animation__scale-up-start, ${scaleStart});



	opacity: var(--intersection-animation_scale-up__not-visible__opacity, ${opacityStart});



}







.intersectionObserve[data-intersection-ananimation="scale-up"]:is(.js-visible, .js-scrolled-up) {



	transform: var(--intersection-animation_scale-up__visible__scale);



  opacity: var(--intersection-animation_scale-up__visible__opacity, 1);



}



    `;

    // SLIDE UP (AND DOWN)

    //

    const slideUpNotVisibleTranslateYVar = 'intersection-animation-slide-up___not-visible__translateY';

    const slideUpNotVisibleTransformVar = 'intersection-animation-slide-up___not-visible__transform';

    const slideUpNotVisibleTranslateY = `translateY(calc(-1 * var(--intersection-animation__slide-vertical-offset, ${slideVerticalOffset}))))`;

    const slideUpNotVisibleTransform = `var(--${slideUpNotVisibleTranslateYVar}, ${slideUpNotVisibleTranslateY})`;

    const slideUpNotVisibleOpacityVar = 'intersection-animation-slide-up__not-visible__opacity';

    //

    const slideDownNotVisibleTranslateYVar = 'intersection-animation-slide-down___not-visible__translateY';

    const slideDownNotVisibleTransformVar = 'intersection-animation-slide-down___not-visible__transform';

    const slideDownNotVisibleTranslateY = `translateY(calc(1 * var(--intersection-animation__slide-vertical-offset, ${slideVerticalOffset})))`;

    const slideDownNotVisibleTransform = `var(--${slideDownNotVisibleTranslateYVar}, ${slideDownNotVisibleTranslateY})`;

    const slideDownNotVisibleOpacityVar = 'intersection-animation-slide-down__not-visible__opacity';

    //

    const slideUpVisibleTranslateYVar = 'intersection-animation-slide-up___visible__translateY';

    const slideUpVisibleTransformVar = 'intersection-animation-slide-up___visible__transform';

    const slideUpVisibleTranslateY = 'translateY(0)';

    const slideUpVisibleTransform = `var(--${slideUpVisibleTranslateYVar}, ${slideUpVisibleTranslateY})`;

    const slideUpVisibleOpacityVar = 'intersection-animation-slide-up__visible__opacity';

    style.innerHTML += `



.intersectionObserve[data-intersection-ananimation="slide-up"]:not(.js-visible):not(.intersectionAnimationDone) {



	transform: var(--${slideUpNotVisibleTransformVar}, ${slideUpNotVisibleTransform};



  opacity: var(--${slideUpNotVisibleOpacityVar}, ${opacityStart});



}







.intersectionObserve[data-intersection-ananimation="slide-up"][data-intersected-direction="up"]:not(.js-visible):not(.intersectionAnimationDone) {



  transform: var(--${slideDownNotVisibleTransformVar}, ${slideDownNotVisibleTransform});



  opacity: var(--${slideDownNotVisibleOpacityVar}, ${opacityStart});



}







.intersectionObserve[data-intersection-ananimation="slide-up"]:is(.js-visible, .js-scrolled-up) {



	transform: var(--${slideUpVisibleTransformVar}, ${slideUpVisibleTransform});



  opacity: var(--${slideUpVisibleOpacityVar}, 1);



}



    `;

    // SLIDE LEFT

    //

    const slideLeftNotVisibleTranslateXVar = 'intersection-animation-slide-left__not-visible__translateX';

    const slideLeftNotVisibleTransformVar = 'intersection-animation-slide-left__not-visible__transform';

    const slideLeftNotVisibleTranslateX = `translateX(calc(-1 * var(--intersection-animation__slide-horizontal-offset, ${slideHorizontalOffset})))`;

    const slideLeftNotVisibleTransform = `var(--${slideLeftNotVisibleTranslateXVar}, ${slideLeftNotVisibleTranslateX})`;

    const slideLeftNotVisibleOpacityVar = 'intersection-animation-slide-left__not-visible__opacity';

    //

    const slideLeftVisibleTranslateXVar = 'intersection-animation-slide-left__visible__translateX';

    const slideLeftVisibleTransformVar = 'intersection-animation-slide-left__visible__transform';

    const slideLeftVisibleTranslateX = 'translateX(0)';

    const slideLeftVisibleTransform = `var(--${slideLeftVisibleTranslateXVar}, ${slideLeftVisibleTranslateX})`;

    const slideLeftVisibleOpacityVar = 'intersection-animation-slide-left__visible__opacity';

    style.innerHTML += `



.intersectionObserve[data-intersection-ananimation="slide-left"]:not(.js-visible):not(.intersectionAnimationDone) {



	transform: var(--${slideLeftNotVisibleTransformVar}, ${slideLeftNotVisibleTransform});



	opacity: var(--${slideLeftNotVisibleOpacityVar}, ${opacityStart});



}







.intersectionObserve[data-intersection-ananimation="slide-left"]:is(.js-visible, .js-scrolled-up) {



	transform: var(--${slideLeftVisibleTransformVar}, ${slideLeftVisibleTransform});



	opacity: var(--${slideLeftVisibleOpacityVar}, 1);



}



    `;

    //SLIDE RIGHT

    const slideRightNotVisibleTranslateXVar = 'intersection-animation-slide-right__not-visible__translateX';

    const slideRightNotVisibleTransformVar = 'intersection-animation-slide-right__not-visible__transform';

    const slideRightNotVisibleTranslateX = `translateX(calc(1 * var(--intersection-animation__slide-horizontal-offset, ${slideHorizontalOffset})))`;

    const slideRightNotVisibleTransform = `var(--${slideRightNotVisibleTranslateXVar}, ${slideRightNotVisibleTranslateX})`;

    const slideRightNotVisibleOpacityVar = 'intersection-animation-slide-right__not-visible__opacity';

    //

    const slideRightVisibleTranslateXVar = 'intersection-animation-slide-right__visible__translateX';

    const slideRightVisibleTransformVar = 'intersection-animation-slide-right__visible__transform';

    const slideRightVisibleTranslateX = 'translateX(0)';

    const slideRightVisibleTransform = `var(--${slideRightVisibleTranslateXVar}, ${slideRightVisibleTranslateX})`;

    const slideRightVisibleOpacityVar = 'intersection-animation-slide-right__visible__opacity';

    style.innerHTML += `



.intersectionObserve[data-intersection-ananimation="slide-right"]:not(.js-visible):not(.intersectionAnimationDone) {



	transform: var(--${slideRightNotVisibleTransformVar}, ${slideRightNotVisibleTransform});



	opacity: var(--${slideRightNotVisibleOpacityVar}, ${opacityStart});



}







.intersectionObserve[data-intersection-ananimation="slide-right"]:is(.js-visible, .js-scrolled-up) {



	transform: var(--${slideRightVisibleTransformVar}, ${slideRightVisibleTransform});



	opacity: var(--${slideRightVisibleOpacityVar}, 1);



}



    `;

    document.head.appendChild(style);
  }

  checkBreakpoints() {
    if (!this.isPlatformBrowser) return;

    const winWidth = window.innerWidth;

    this.observables.forEach(observerItem => {
      const { breakpoint, targets } = observerItem;

      if (!breakpoint) return;

      const isMobile = winWidth < breakpoint - 1;

      targets.forEach(target => {
        const el = target.element;

        const animation = isMobile ? target.animationMobile || target.animation : target.animation;

        if (animation) this.setAttrAnimationToElement(el, animation);
      });
    });
  }

  private setAttrAnimationToElement(el: HTMLElement, animation: IntersectionAnimations) {
    el.setAttribute('data-intersection-ananimation', animation);
  }

  private generateKey(): string {
    const genId = () => Math.random().toString(36).substring(2, 15).substring(7);

    const id = genId();

    if (this.observables.has(id)) {
      return this.generateKey();
    }

    return id;
  }
}
